import React, { useState, useEffect } from 'react';
import { getReferralStatus, applyReferralCode, changeReferral } from '../../services/api';

function ReferralSection() {
  const [referralData, setReferralData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // 'success' or 'error'
  const [showChangeInput, setShowChangeInput] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const data = await getReferralStatus();
      setReferralData(data);
    } catch (err) {
      console.error('Failed to load referral status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyCode = async () => {
    if (!code.trim()) return;

    setSubmitting(true);
    setMessage('');
    try {
      await applyReferralCode(code.trim());
      setMessage('Referral code applied successfully.');
      setMessageType('success');
      setCode('');
      await fetchStatus();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to apply referral code.';
      setMessage(errorMsg);
      setMessageType('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangeReferral = async () => {
    if (!code.trim()) return;

    setSubmitting(true);
    setMessage('');
    try {
      await changeReferral(code.trim());
      setMessage('Referral changed successfully.');
      setMessageType('success');
      setCode('');
      setShowChangeInput(false);
      await fetchStatus();
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to change referral.';
      setMessage(errorMsg);
      setMessageType('error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return null;
  }

  // No referral — show input to apply one
  if (!referralData?.has_referral) {
    return (
      <section className="bg-surface rounded-lg border border-muted p-6 mb-6">
        <h2 className="text-lg font-semibold text-brand-white mb-4">Referral Attribution</h2>
        <p className="text-muted text-sm mb-3">
          Were you referred by someone? Enter their code to support them.
        </p>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Enter referral code..."
            style={{
              padding: '8px 12px',
              backgroundColor: '#111',
              border: '1px solid #333',
              borderRadius: '6px',
              color: '#fff',
              fontSize: '14px',
              width: '220px',
            }}
          />
          <button
            onClick={handleApplyCode}
            disabled={submitting || !code.trim()}
            style={{
              padding: '8px 16px',
              backgroundColor: '#218aff',
              border: 'none',
              borderRadius: '6px',
              color: '#fff',
              fontSize: '13px',
              cursor: submitting || !code.trim() ? 'not-allowed' : 'pointer',
              opacity: submitting || !code.trim() ? 0.6 : 1,
            }}
          >
            {submitting ? 'Applying...' : 'Apply Code'}
          </button>
        </div>
        {message && (
          <p style={{ marginTop: '8px', fontSize: '13px', color: messageType === 'error' ? '#ef4444' : '#10b981' }}>
            {message}
          </p>
        )}
      </section>
    );
  }

  // Has referral — show status
  const lockDate = new Date(referralData.locked_until);
  const attributedDate = new Date(referralData.attributed_at);

  return (
    <section className="bg-surface rounded-lg border border-muted p-6 mb-6">
      <h2 className="text-lg font-semibold text-brand-white mb-4">Referral Attribution</h2>

      <div className="space-y-3">
        {referralData.affiliate_code ? (
          <>
            <div>
              <label className="block text-sm text-muted mb-1">Referred By</label>
              <p className="text-brand-white" style={{ fontWeight: '600' }}>
                {referralData.affiliate_code}
              </p>
            </div>
            <div>
              <label className="block text-sm text-muted mb-1">Attributed Since</label>
              <p className="text-brand-white">
                {attributedDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
            <div>
              <label className="block text-sm text-muted mb-1">Attribution Status</label>
              {referralData.is_locked ? (
                <p className="text-sm" style={{ color: '#fbbf24' }}>
                  Locked until {lockDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              ) : (
                <p className="text-sm" style={{ color: '#10b981' }}>
                  Unlocked — you can change your referral
                </p>
              )}
            </div>

            {/* Change referral (only if unlocked) */}
            {referralData.can_change && !showChangeInput && (
              <button
                onClick={() => setShowChangeInput(true)}
                style={{
                  padding: '6px 14px',
                  backgroundColor: 'transparent',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: '#ccc',
                  fontSize: '13px',
                  cursor: 'pointer',
                  marginTop: '8px',
                }}
              >
                Change Referral
              </button>
            )}

            {showChangeInput && (
              <div style={{ marginTop: '8px' }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="Enter new referral code..."
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#111',
                      border: '1px solid #333',
                      borderRadius: '6px',
                      color: '#fff',
                      fontSize: '14px',
                      width: '220px',
                    }}
                  />
                  <button
                    onClick={handleChangeReferral}
                    disabled={submitting || !code.trim()}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#218aff',
                      border: 'none',
                      borderRadius: '6px',
                      color: '#fff',
                      fontSize: '13px',
                      cursor: submitting || !code.trim() ? 'not-allowed' : 'pointer',
                      opacity: submitting || !code.trim() ? 0.6 : 1,
                    }}
                  >
                    {submitting ? 'Changing...' : 'Change'}
                  </button>
                  <button
                    onClick={() => { setShowChangeInput(false); setCode(''); setMessage(''); }}
                    style={{
                      padding: '8px 14px',
                      backgroundColor: 'transparent',
                      border: '1px solid #333',
                      borderRadius: '6px',
                      color: '#999',
                      fontSize: '13px',
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
                <p className="text-xs mt-1" style={{ color: '#666' }}>
                  Changing your referral will lock the new attribution for 2 years.
                </p>
              </div>
            )}
          </>
        ) : (
          <>
            <p className="text-muted text-sm">
              Your previous referral is no longer active. You can enter a new referral code.
            </p>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter new referral code..."
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#111',
                  border: '1px solid #333',
                  borderRadius: '6px',
                  color: '#fff',
                  fontSize: '14px',
                  width: '220px',
                }}
              />
              <button
                onClick={handleChangeReferral}
                disabled={submitting || !code.trim()}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#218aff',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#fff',
                  fontSize: '13px',
                  cursor: submitting || !code.trim() ? 'not-allowed' : 'pointer',
                  opacity: submitting || !code.trim() ? 0.6 : 1,
                }}
              >
                {submitting ? 'Applying...' : 'Apply Code'}
              </button>
            </div>
          </>
        )}

        {message && (
          <p style={{ fontSize: '13px', color: messageType === 'error' ? '#ef4444' : '#10b981' }}>
            {message}
          </p>
        )}
      </div>
    </section>
  );
}

export default ReferralSection;
